import React, { type PropsWithChildren, useEffect } from 'react';

import { useNotifications } from '@jira/polaris__lib-notifications/src/useNotifications.tsx';
import { useProductSettingsActions } from '@jira/polaris__component-product-settings-store/src/useProductSettingsActions.tsx';
import { useViewBlueprintsEnabled } from '@jira/polaris__component-product-settings-store/src/useDatesInitialMonth.tsx';
import {
	useCanCreateProject,
	useCanManageViews,
} from '@jira/polaris__component-permissions-store/src/useCanEditIssues.tsx';
import { useIsCollectionView } from '@jira/polaris__component-environment/src/PolarisEnvironmentProvider.tsx';
import { useIntl, defineMessage } from '@atlassian/jira-intl';

const loadErrorTitle = defineMessage({
	id: 'polaris-component-view-blueprints.page.load.error.title',
	defaultMessage: 'Failed to load view blueprints',
	description: 'Error title shown when loading view blueprints setting fails',
});

const loadErrorDescription = defineMessage({
	id: 'polaris-component-view-blueprints.page.load.error.description',
	defaultMessage: 'Please try again',
	description: 'Error description shown when loading view blueprints setting fails',
});

export const ViewBlueprintsButtonGuard = ({
	children,
}: PropsWithChildren): React.JSX.Element | null => {
	const { error } = useNotifications();
	const { formatMessage } = useIntl();
	const canCreateProject = useCanCreateProject();
	const canManageProject = useCanManageViews();
	const { loadProductSettings } = useProductSettingsActions();
	const isCollectionView = useIsCollectionView();
	const viewBlueprintsEnabled = useViewBlueprintsEnabled();

	useEffect(() => {
		if (!canCreateProject || !canManageProject) {
			return;
		}

		loadProductSettings().catch(() => {
			error({
				title: formatMessage(loadErrorTitle),
				description: formatMessage(loadErrorDescription),
			});
		});
	}, [loadProductSettings, error, formatMessage, canCreateProject, canManageProject]);

	// Check user permissions
	const hasRequiredPermissions = canCreateProject && canManageProject;

	// Hide if requirements aren't met
	if (!hasRequiredPermissions || !viewBlueprintsEnabled) {
		return null;
	}

	if (isCollectionView) {
		return null;
	}

	return <>{children}</>;
};
